import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { acquireBodyScrollLock } from '../../lib/bodyScrollLock';

interface AccessibleDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  /** Set to false to keep the dialog open when the user presses Escape. Default true. */
  closeOnEscape?: boolean;
  /** Set to false to keep the dialog open when the user clicks the backdrop. Default true. */
  closeOnBackdrop?: boolean;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  /**
   * Visual variant. `modal` is a centered dialog (default); `drawer-right`
   * is a side panel that slides in from the right. Both share the same a11y
   * guarantees: focus trap, restore-on-close, role=dialog, aria-modal.
   */
  variant?: 'modal' | 'drawer-right';
  /** Render label/description chrome inside the dialog (default true). When
   * false, the consumer provides its own header but the title is still
   * announced via aria-label. */
  renderChrome?: boolean;
  /** Hide the close button (rendered when chrome is shown). */
  hideCloseButton?: boolean;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/**
 * Minimal accessible modal dialog primitive. Implements:
 *  - role="dialog" + aria-modal
 *  - Escape to close (configurable)
 *  - Backdrop click to close (configurable)
 *  - Focus trap inside the dialog while open
 *  - Restores focus to the previously focused element on close
 *
 * This is a small, dependency-free Radix-style primitive so we don't ship a
 * heavier UI lib in v1. Routes that need richer interactions (e.g. nested
 * popovers) should still use this as the wrapper to keep a11y guarantees.
 */
export default function AccessibleDialog({
  open,
  onClose,
  title,
  description,
  children,
  closeOnEscape = true,
  closeOnBackdrop = true,
  ariaLabelledBy,
  ariaDescribedBy,
  variant = 'modal',
  renderChrome = true,
  hideCloseButton = false
}: AccessibleDialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const root = dialogRef.current;
    if (root) {
      const first = root.querySelector<HTMLElement>(FOCUSABLE);
      (first || root).focus();
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key === 'Tab' && root) {
        const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (nodes.length === 0) {
          event.preventDefault();
          return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const releaseScrollLock = acquireBodyScrollLock();

    return () => {
      document.removeEventListener('keydown', onKey);
      releaseScrollLock();
      const last = previouslyFocused.current;
      if (last && typeof last.focus === 'function') {
        last.focus();
      }
    };
  }, [open, onClose, closeOnEscape]);

  if (!open) return null;

  const labelId = ariaLabelledBy || 'accessible-dialog-title';
  const descId = ariaDescribedBy || (description ? 'accessible-dialog-description' : undefined);

  const isDrawer = variant === 'drawer-right';
  const containerClass = isDrawer
    ? 'fixed inset-0 z-[12000] flex items-stretch justify-end bg-black/70'
    : 'fixed inset-0 z-[12000] flex items-center justify-center bg-black/70 p-4';
  const panelClass = isDrawer
    ? 'h-full w-full max-w-xl bg-ninpo-black border-l border-white/10 text-white shadow-2xl outline-none overflow-y-auto'
    : 'bg-zinc-900 text-white rounded-lg shadow-2xl w-full max-w-md p-6 outline-none';

  return createPortal(
    <div
      role="button"
      tabIndex={0}
      className={containerClass}
      onClick={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget && (event.key === 'Escape' || event.key === 'Enter')) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        aria-describedby={descId}
        tabIndex={-1}
        className={panelClass}
      >
        {renderChrome ? (
          <div className={isDrawer ? 'p-5 space-y-4' : ''}>
            <div className="flex items-center justify-between">
              <h2 id={labelId} className={isDrawer ? 'text-sm font-black uppercase tracking-widest' : 'text-xl font-black uppercase mb-2'}>
                {title}
              </h2>
              {!hideCloseButton ? (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={`Close ${title}`}
                  className="text-slate-400 hover:text-white text-sm"
                >
                  ✕
                </button>
              ) : null}
            </div>
            {description ? (
              <p id={descId} className={isDrawer ? 'text-xs text-zinc-400' : 'text-sm text-zinc-400 mb-4'}>{description}</p>
            ) : null}
            <div>{children}</div>
          </div>
        ) : (
          <>
            <h2 id={labelId} className="sr-only">{title}</h2>
            {description ? <p id={descId} className="sr-only">{description}</p> : null}
            {children}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
